import { useEffect, useRef, useState } from 'react';

import { ArrowRight, Loader2, Mic, Square } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useVoiceNote } from '@/hooks/useVoiceNote';
import { stockAssistantApi } from '@/lib/api/stock-assistant.api';
import { RATE_LIMIT_MESSAGE, statusOf } from '@/lib/stock-assistant-errors';

import { StockCarCardView } from './StockCarCard';

import type { StockCarCard, StockSearchState } from '@repo/shared-types';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  cars?: StockCarCard[];
  error?: boolean;
}

const STARTERS = ['Lowest mileage?', 'Compare the top 3', 'Which is best value?'];
const MAX_HISTORY = 20;

export const ResultsChatDrawer = ({
  open,
  onOpenChange,
  state,
  sources,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  state: StockSearchState;
  sources: string[];
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [text, setText] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const abort = useRef<AbortController | null>(null);
  const end = useRef<HTMLDivElement>(null);
  const voice = useVoiceNote({ onText: (t) => setText(t) });

  // Braces matter: Chrome's scrollIntoView returns a Promise, which React would treat as a cleanup.
  useEffect(() => {
    end.current?.scrollIntoView?.({ block: 'end' });
  }, [messages, status]);
  useEffect(() => () => abort.current?.abort(), []);

  const ask = async (question: string) => {
    const content = question.trim();
    if (!content || busy) return;
    // Empty or errored assistant messages (a failed or aborted stream) are never sent back as history.
    const history: Message[] = [
      ...messages.filter((m) => m.role === 'user' || (!m.error && m.content.trim())),
      { role: 'user' as const, content },
    ].slice(-MAX_HISTORY);
    setMessages([...history, { role: 'assistant', content: '', cars: [] }]);
    setText('');
    setBusy(true);
    abort.current?.abort();
    const controller = new AbortController();
    abort.current = controller;
    const update = (fn: (m: Message) => Message) => {
      if (controller.signal.aborted) return;
      setMessages((all) => [...all.slice(0, -1), fn(all[all.length - 1])]);
    };
    try {
      await stockAssistantApi.chat(
        { messages: history.map(({ role, content: c }) => ({ role, content: c })), state, sources },
        (event) => {
          if (controller.signal.aborted) return;
          if (event.type === 'status') setStatus(event.message);
          if (event.type === 'cars') {
            update((m) => ({ ...m, cars: [...(m.cars ?? []), ...event.cars].slice(0, 6) }));
          }
          if (event.type === 'response') update((m) => ({ ...m, content: event.content }));
          if (event.type === 'error')
            update((m) => ({ ...m, content: event.message, error: true }));
        },
        controller.signal,
      );
    } catch (err) {
      update((m) => ({
        ...m,
        content:
          statusOf(err) === 429
            ? RATE_LIMIT_MESSAGE
            : 'The assistant is unavailable — try again shortly.',
        error: true,
      }));
    } finally {
      if (abort.current === controller) {
        setBusy(false);
        setStatus(null);
      }
    }
  };

  const recording = voice.status === 'recording';
  // Closing the sheet must not leave the microphone running.
  const stopVoice = voice.stop;
  useEffect(() => {
    if (!open && voice.status === 'recording') stopVoice();
  }, [open, voice.status, stopVoice]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right">
        <SheetHeader>
          <SheetTitle>Ask about these results</SheetTitle>
          <SheetDescription>
            Answers use the same live stock search. Nothing is saved.
          </SheetDescription>
        </SheetHeader>
        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          {messages.length === 0 && (
            <div className="flex flex-wrap gap-2">
              {STARTERS.map((s) => (
                <Button key={s} variant="outline" size="sm" onClick={() => void ask(s)}>
                  {s}
                </Button>
              ))}
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'space-y-2'}>
              {m.content && (
                <p
                  className={
                    m.role === 'user'
                      ? 'bg-primary text-primary-foreground max-w-[85%] rounded-2xl rounded-br-sm px-3 py-2 text-sm'
                      : 'text-sm leading-relaxed'
                  }
                >
                  {m.content}
                </p>
              )}
              {m.cars?.map((car) => (
                <StockCarCardView key={car.id} car={car} compact newTab />
              ))}
            </div>
          ))}
          <p aria-live="polite" className="text-muted-foreground flex items-center gap-2 text-sm">
            {status && (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                {status}
              </>
            )}
          </p>
          <div ref={end} />
        </div>
        <form
          className="flex items-center gap-2 border-t p-3"
          onSubmit={(e) => {
            e.preventDefault();
            void ask(text);
          }}
        >
          <label htmlFor="results-chat" className="sr-only">
            Your question
          </label>
          <input
            id="results-chat"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Ask a question…"
            className="border-input bg-background focus-visible:ring-ring h-10 min-w-0 flex-1 rounded-md border px-3 text-base focus-visible:ring-2 focus-visible:outline-none"
            disabled={recording}
          />
          <Button
            type="button"
            size="icon"
            variant={recording ? 'destructive' : 'ghost'}
            aria-label={recording ? 'Stop recording' : 'Record a voice note'}
            disabled={
              !voice.supported ||
              voice.status === 'blocked' ||
              voice.status === 'unsupported' ||
              voice.status === 'transcribing' ||
              voice.starting ||
              busy
            }
            onClick={() => (recording ? voice.stop() : void voice.start())}
          >
            {recording ? (
              <Square className="h-4 w-4" aria-hidden />
            ) : (
              <Mic className="h-4 w-4" aria-hidden />
            )}
          </Button>
          <Button
            type="submit"
            size="icon"
            aria-label="Send question"
            disabled={!text.trim() || busy}
          >
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  );
};
